import { ResponsiveTable } from '../components/ResponsiveTable';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl, apiClient } from '../utils/api';
import { useShopScopedPage } from '../hooks/useShopScopedReload';
import { ShopSelectionGate } from '../components/ShopSelectionGate';
import { Mail, ChevronUp, ChevronDown, Filter, ExternalLink, Trash2 } from 'lucide-react';
import { ConfirmModal } from '../components/ConfirmModal';
import { ConfirmDefinitiveDeleteModal } from '../components/ConfirmDefinitiveDeleteModal';
import { useVisibleSelection } from '../hooks/useVisibleSelection';
import { useAdminLogBulkDelete } from '../hooks/useAdminLogBulkDelete';
import { DateInput } from '../components/DateInput';

interface EmailLog {
  id: string;
  to: string;
  subject: string;
  templateType: string | null;
  context: string | null;
  success: boolean;
  sendgridStatus: number | null;
  sendgridBody: string | null;
  sendgridHeaders: Record<string, string> | null;
  errorMessage: string | null;
  htmlPreview: string | null;
  htmlBody: string | null;
  rawMessage: string | null;
  createdAt: string;
}

const SORT_FIELDS = [
  { value: 'createdAt', labelKey: 'admin.emailLogs.sort.createdAt' },
  { value: 'to', labelKey: 'admin.emailLogs.sort.to' },
  { value: 'subject', labelKey: 'admin.emailLogs.sort.subject' },
  { value: 'templateType', labelKey: 'admin.emailLogs.sort.templateType' },
  { value: 'success', labelKey: 'admin.emailLogs.sort.success' },
  { value: 'sendgridStatus', labelKey: 'admin.emailLogs.sort.sendgridStatus' },
] as const;

export function EmailLogsPage() {
  const { t } = useTranslation();
  const { needsShopSelection, canLoad, currentShopId } = useShopScopedPage();
  const [items, setItems] = useState<EmailLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [selectedLog, setSelectedLog] = useState<EmailLog | null>(null);
  const [filters, setFilters] = useState({
    to: '',
    subject: '',
    templateType: '',
    success: '',
    dateFrom: '',
    dateTo: '',
    limit: '50',
    offset: '0',
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const loadEmailLogs = useCallback(async () => {
    if (!canLoad) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await apiClient(`/api/admin/email-logs?${params}`);

      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Accès refusé. Réservé aux administrateurs et super administrateurs.');
        }
        throw new Error('Échec du chargement des emails');
      }

      const data = await response.json();
      if (data.success) {
        setItems(data.data.items || []);
        setTotal(data.data.total || 0);
      }
    } catch (error) {
      console.error('Failed to load email logs:', error);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [filters, canLoad]);

  useEffect(() => {
    loadEmailLogs();
  }, [loadEmailLogs, currentShopId]);

  const visibleIds = useMemo(() => items.map((item) => item.id), [items]);
  const {
    selectedArray,
    selectedCount,
    clearSelection,
    selectAllCheckboxRef,
    allVisibleSelected,
    toggleSelect,
    toggleSelectAllVisible,
  } = useVisibleSelection<string>(visibleIds);

  const bulkDelete = useAdminLogBulkDelete({
    softDeletePath: '/api/admin/email-logs/bulk-soft-delete',
    hardDeletePath: '/api/admin/email-logs/bulk-hard-delete',
    i18n: {
      error: 'admin.emailLogs.bulkDelete.error',
      partialSoft: 'admin.emailLogs.bulkDelete.partialSoft',
      partialHard: 'admin.emailLogs.bulkDelete.partialHard',
      softSuccess: 'admin.emailLogs.bulkDelete.softSuccess',
      hardSuccess: 'admin.emailLogs.bulkDelete.hardSuccess',
      softTitle: 'admin.emailLogs.bulkDelete.softTitle',
      softMessage: 'admin.emailLogs.bulkDelete.softMessage',
      entityLabel: 'admin.emailLogs.bulkDelete.entityLabel',
    },
    onComplete: async () => {
      clearSelection();
      await loadEmailLogs();
    },
  });

  const formatDateTime = (dateTime: string) =>
    new Date(dateTime).toLocaleString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });

  const handleSort = (field: string) => {
    setFilters((prev) => ({
      ...prev,
      sortBy: field,
      sortOrder:
        prev.sortBy === field && prev.sortOrder === 'desc' ? 'asc' : 'desc',
    }));
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (filters.sortBy !== field) return null;
    return filters.sortOrder === 'asc' ? (
      <ChevronUp className="w-4 h-4 inline ml-1" />
    ) : (
      <ChevronDown className="w-4 h-4 inline ml-1" />
    );
  };

  if (needsShopSelection) {
    return <ShopSelectionGate />;
  }

  return (
    <div className="space-y-6">
      <div className="dt-list-toolbar flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-textPrimary flex items-center gap-2">
            <Mail className="w-7 h-7" />
            {t('admin.emailLogs.title')}
          </h1>
          <p className="text-textSecondary mt-1">{t('admin.emailLogs.subtitle')}</p>
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="btn-secondary flex items-center gap-2"
        >
          <Filter className="w-4 h-4" />
          {t('admin.emailLogs.filters')}
        </button>
      </div>

      {showFilters && (
        <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
          <div className="dt-filters grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-1">
                {t('admin.emailLogs.filter.to')}
              </label>
              <input
                type="text"
                value={filters.to}
                onChange={(e) => setFilters({ ...filters, to: e.target.value })}
                placeholder="email@..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-1">
                {t('admin.emailLogs.filter.subject')}
              </label>
              <input
                type="text"
                value={filters.subject}
                onChange={(e) =>
                  setFilters({ ...filters, subject: e.target.value })
                }
                placeholder="Rechercher..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-1">
                {t('admin.emailLogs.filter.templateType')}
              </label>
              <input
                type="text"
                value={filters.templateType}
                onChange={(e) =>
                  setFilters({ ...filters, templateType: e.target.value })
                }
                placeholder="verification, piercing_otp..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-1">
                {t('admin.emailLogs.filter.success')}
              </label>
              <select
                value={filters.success}
                onChange={(e) =>
                  setFilters({ ...filters, success: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">{t('admin.emailLogs.filter.all')}</option>
                <option value="true">{t('admin.emailLogs.filter.successYes')}</option>
                <option value="false">{t('admin.emailLogs.filter.successNo')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-1">
                {t('admin.emailLogs.filter.dateFrom')}
              </label>
              <DateInput
                value={filters.dateFrom}
                onChange={(value) =>
                  setFilters({ ...filters, dateFrom: value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-1">
                {t('admin.emailLogs.filter.dateTo')}
              </label>
              <DateInput
                value={filters.dateTo}
                onChange={(value) =>
                  setFilters({ ...filters, dateTo: value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-1">
                {t('admin.emailLogs.filter.sortBy')}
              </label>
              <select
                value={filters.sortBy}
                onChange={(e) =>
                  setFilters({ ...filters, sortBy: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {SORT_FIELDS.map((f) => (
                  <option key={f.value} value={f.value}>
                    {t(f.labelKey)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-1">
                {t('admin.emailLogs.filter.sortOrder')}
              </label>
              <select
                value={filters.sortOrder}
                onChange={(e) =>
                  setFilters({ ...filters, sortOrder: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="desc">{t('admin.emailLogs.filter.desc')}</option>
                <option value="asc">{t('admin.emailLogs.filter.asc')}</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-textPrimary mb-1">
                {t('admin.emailLogs.filter.limit')}
              </label>
              <select
                value={filters.limit}
                onChange={(e) =>
                  setFilters({ ...filters, limit: e.target.value, offset: '0' })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
              </select>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex flex-wrap justify-between items-center gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm text-textSecondary">
              {t('admin.emailLogs.showing', {
                count: items.length,
                total,
                offset: parseInt(filters.offset, 10) || 1,
              })}
            </p>
            {selectedCount > 0 && (
              <span className="text-sm text-primary-600 font-medium">
                {t('admin.emailLogs.bulkDelete.selected', { count: selectedCount })}
              </span>
            )}
          </div>
          {selectedCount > 0 && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => bulkDelete.setSoftDeleteModalOpen(true)}
                disabled={bulkDelete.deleteLoading}
                className="btn-secondary text-sm inline-flex items-center gap-1.5 disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" />
                {t('admin.emailLogs.bulkDelete.soft', { count: selectedCount })}
              </button>
              {bulkDelete.canHardDelete && (
                <button
                  type="button"
                  onClick={() => bulkDelete.setHardDeleteModalOpen(true)}
                  disabled={bulkDelete.deleteLoading}
                  className="text-sm inline-flex items-center gap-1.5 px-3 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50 disabled:opacity-50"
                >
                  <Trash2 className="w-4 h-4" />
                  {t('admin.emailLogs.bulkDelete.hard', { count: selectedCount })}
                </button>
              )}
            </div>
          )}
        </div>

        {loading ? (
          <div className="p-12 text-center text-textSecondary">
            {t('admin.emailLogs.loading')}
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-textSecondary">
            {t('admin.emailLogs.empty')}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <ResponsiveTable className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      ref={selectAllCheckboxRef}
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAllVisible}
                      disabled={items.length === 0}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      aria-label={t('admin.emailLogs.bulkDelete.selectAll')}
                    />
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('createdAt')}
                      className="text-xs font-semibold text-textPrimary uppercase tracking-wider hover:text-primary-600"
                    >
                      {t('admin.emailLogs.sort.createdAt')}
                      <SortIcon field="createdAt" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('to')}
                      className="text-xs font-semibold text-textPrimary uppercase tracking-wider hover:text-primary-600"
                    >
                      {t('admin.emailLogs.sort.to')}
                      <SortIcon field="to" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('subject')}
                      className="text-xs font-semibold text-textPrimary uppercase tracking-wider hover:text-primary-600"
                    >
                      {t('admin.emailLogs.sort.subject')}
                      <SortIcon field="subject" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('templateType')}
                      className="text-xs font-semibold text-textPrimary uppercase tracking-wider hover:text-primary-600"
                    >
                      {t('admin.emailLogs.sort.templateType')}
                      <SortIcon field="templateType" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('success')}
                      className="text-xs font-semibold text-textPrimary uppercase tracking-wider hover:text-primary-600"
                    >
                      {t('admin.emailLogs.sort.success')}
                      <SortIcon field="success" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left">
                    <button
                      onClick={() => handleSort('sendgridStatus')}
                      className="text-xs font-semibold text-textPrimary uppercase tracking-wider hover:text-primary-600"
                    >
                      {t('admin.emailLogs.sort.sendgridStatus')}
                      <SortIcon field="sendgridStatus" />
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-textPrimary uppercase tracking-wider">
                    {t('admin.emailLogs.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map((log) => (
                  <tr key={log.id} className={`hover:bg-gray-50 ${selectedArray.includes(log.id) ? 'bg-primary-50/40' : ''}`}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedArray.includes(log.id)}
                        onChange={() => toggleSelect(log.id)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        aria-label={t('admin.emailLogs.bulkDelete.selectRow')}
                      />
                    </td>
                    <td className="px-4 py-3 text-sm text-textSecondary whitespace-nowrap">
                      {formatDateTime(log.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-sm text-textPrimary">
                      <span className="font-mono">{log.to}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-textPrimary max-w-xs truncate">
                      {log.subject}
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 text-xs font-medium rounded bg-gray-100 text-gray-800">
                        {log.templateType || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 text-xs font-semibold rounded ${
                          log.success
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {log.success ? '✓' : '✗'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span
                        className={`font-mono font-medium ${
                          (log.sendgridStatus ?? 0) >= 200 &&
                          (log.sendgridStatus ?? 0) < 300
                            ? 'text-green-600'
                            : (log.sendgridStatus ?? 0) > 0
                            ? 'text-red-600'
                            : 'text-gray-500'
                        }`}
                      >
                        {log.sendgridStatus ?? '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setSelectedLog(log)}
                        className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center gap-1"
                      >
                        <ExternalLink className="w-4 h-4" />
                        {t('admin.emailLogs.viewDetails')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </ResponsiveTable>
          </div>
        )}

        {total > parseInt(filters.limit, 10) && (
          <div className="dt-list-toolbar px-4 py-3 border-t border-gray-200 flex justify-between items-center">
            <button
              disabled={parseInt(filters.offset, 10) === 0}
              onClick={() =>
                setFilters({
                  ...filters,
                  offset: String(
                    Math.max(0, parseInt(filters.offset, 10) - parseInt(filters.limit, 10))
                  ),
                })
              }
              className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.previous')}
            </button>
            <span className="text-sm text-textSecondary">
              {t('admin.emailLogs.pageInfo', {
                from: parseInt(filters.offset, 10) + 1,
                to: Math.min(
                  parseInt(filters.offset, 10) + items.length,
                  total
                ),
                total,
              })}
            </span>
            <button
              disabled={
                parseInt(filters.offset, 10) + items.length >= total
              }
              onClick={() =>
                setFilters({
                  ...filters,
                  offset: String(
                    parseInt(filters.offset, 10) + parseInt(filters.limit, 10)
                  ),
                })
              }
              className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t('common.next')}
            </button>
          </div>
        )}
      </div>

      {selectedLog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-textPrimary">
                {t('admin.emailLogs.detailTitle')} – {selectedLog.id}
              </h2>
              <button
                onClick={() => setSelectedLog(null)}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="p-6 overflow-y-auto space-y-4">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="font-medium text-gray-600">{t('admin.emailLogs.sort.to')}:</span>
                  <span className="ml-2 font-mono">{selectedLog.to}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">{t('admin.emailLogs.sort.createdAt')}:</span>
                  <span className="ml-2">{formatDateTime(selectedLog.createdAt)}</span>
                </div>
                <div className="col-span-2">
                  <span className="font-medium text-gray-600">{t('admin.emailLogs.sort.subject')}:</span>
                  <p className="mt-1 text-textPrimary">{selectedLog.subject}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-600">{t('admin.emailLogs.sort.templateType')}:</span>
                  <span className="ml-2">{selectedLog.templateType || '-'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">{t('admin.emailLogs.sort.success')}:</span>
                  <span className="ml-2">{selectedLog.success ? '✓' : '✗'}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">{t('admin.emailLogs.sort.sendgridStatus')}:</span>
                  <span className="ml-2">{selectedLog.sendgridStatus ?? '-'}</span>
                </div>
                {selectedLog.context && (
                  <div className="col-span-2">
                    <span className="font-medium text-gray-600">Context:</span>
                    <p className="mt-1 text-textPrimary font-mono text-xs">{selectedLog.context}</p>
                  </div>
                )}
              </div>
              {selectedLog.errorMessage && (
                <div>
                  <h3 className="font-semibold text-red-700 mb-1">{t('admin.emailLogs.errorMessage')}</h3>
                  <pre className="bg-red-50 p-3 rounded text-sm text-red-800 overflow-x-auto max-h-32">
                    {selectedLog.errorMessage}
                  </pre>
                </div>
              )}
              {selectedLog.sendgridBody && (
                <div>
                  <h3 className="font-semibold text-textPrimary mb-1">{t('admin.emailLogs.sendgridResponse')}</h3>
                  <pre className="bg-gray-50 p-3 rounded text-xs overflow-x-auto max-h-48 whitespace-pre-wrap break-all">
                    {selectedLog.sendgridBody}
                  </pre>
                </div>
              )}
              {selectedLog.sendgridHeaders && Object.keys(selectedLog.sendgridHeaders).length > 0 && (
                <div>
                  <h3 className="font-semibold text-textPrimary mb-1">{t('admin.emailLogs.sendgridHeaders')}</h3>
                  <pre className="bg-gray-50 p-3 rounded text-xs overflow-x-auto max-h-32">
                    {JSON.stringify(selectedLog.sendgridHeaders, null, 2)}
                  </pre>
                </div>
              )}
              {(selectedLog.htmlBody || selectedLog.htmlPreview) && (
                <div>
                  <h3 className="font-semibold text-textPrimary mb-1">
                    {selectedLog.htmlBody
                      ? t('admin.emailLogs.fullContent')
                      : t('admin.emailLogs.htmlPreview')}
                  </h3>
                  <div
                    className="bg-gray-50 p-4 rounded text-sm overflow-x-auto max-h-[400px] overflow-y-auto border border-gray-200 [&_a]:text-primary-600 [&_a]:underline"
                    dangerouslySetInnerHTML={{
                      __html: selectedLog.htmlBody || selectedLog.htmlPreview || '',
                    }}
                  />
                </div>
              )}
              {selectedLog.rawMessage && (
                <div>
                  <h3 className="font-semibold text-textPrimary mb-1">
                    {t('admin.emailLogs.rawMessage')}
                  </h3>
                  <pre className="bg-slate-900 text-slate-100 p-4 rounded text-xs overflow-x-auto max-h-[400px] overflow-y-auto border border-slate-700 whitespace-pre-wrap break-all font-mono">
                    {selectedLog.rawMessage}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {bulkDelete.toast && (
        <div
          className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm text-white ${
            bulkDelete.toast.type === 'success'
              ? 'bg-green-600'
              : bulkDelete.toast.type === 'warning'
                ? 'bg-amber-600'
                : 'bg-red-600'
          }`}
        >
          {bulkDelete.toast.message}
        </div>
      )}

      <ConfirmModal
        isOpen={bulkDelete.softDeleteModalOpen}
        title={t(bulkDelete.i18n.softTitle)}
        message={t(bulkDelete.i18n.softMessage, { count: selectedCount })}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        confirmVariant="warning"
        isLoading={bulkDelete.deleteLoading}
        onConfirm={() => bulkDelete.confirmBulkSoftDelete(selectedArray)}
        onClose={() => !bulkDelete.deleteLoading && bulkDelete.setSoftDeleteModalOpen(false)}
      />

      <ConfirmDefinitiveDeleteModal
        isOpen={bulkDelete.hardDeleteModalOpen}
        onConfirm={() => bulkDelete.confirmBulkHardDelete(selectedArray)}
        onClose={() => !bulkDelete.deleteLoading && bulkDelete.setHardDeleteModalOpen(false)}
        entityLabel={t(bulkDelete.i18n.entityLabel)}
        count={selectedCount}
        isLoading={bulkDelete.deleteLoading}
      />
    </div>
  );
}
